import { useEffect, useMemo } from 'react';
import { Download, LineChart } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { EmptyState } from '../../../components/EmptyState';
import { useAssetStore, type Asset } from '../../../store/assetStore';
import { useAssetSettingsStore, type AssetSettings } from '../../../store/assetSettingsStore';

/** Useful life for an asset from the depreciation settings, by category. */
function usefulLife(a: Asset, d: AssetSettings['depreciation']) {
  const cat = `${a.category || ''} ${a.type || ''}`.toLowerCase();
  if (/server|storage|network/.test(cat)) return d.server_useful_life_years;
  if (/mobile|phone|tablet/.test(cat)) return d.mobile_useful_life_years;
  if (/monitor|peripheral|audio|printer/.test(cat)) return d.peripheral_useful_life_years;
  return d.laptop_useful_life_years;
}

/** Straight-line book value today (cost − accumulated depreciation, not below salvage). */
function bookValue(a: Asset, d: AssetSettings['depreciation']) {
  if (a.purchaseCost === null || a.purchaseCost === undefined) return null;
  if (d.method === 'No Depreciation' || !a.purchaseDate) return a.purchaseCost;
  const salvage = a.specifications.salvageValue !== undefined && a.specifications.salvageValue !== ''
    ? Number(a.specifications.salvageValue)
    : a.purchaseCost * (d.default_salvage_percentage / 100);
  const years = usefulLife(a, d);
  if (!years) return a.purchaseCost;
  const [y, m, day] = a.purchaseDate.split('-').map(Number);
  const months = Math.max(0, (Date.now() - new Date(y, m - 1, day).getTime()) / (30.4375 * 86_400_000));
  const perMonth = (a.purchaseCost - salvage) / (years * 12);
  return Math.max(salvage, a.purchaseCost - perMonth * months);
}

function toCsv(rows: (string | number | null | undefined)[][]) {
  return rows.map((r) => r.map((v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\n');
}

/**
 * Asset reports — computed from the register (assets.assets), service jobs
 * (assets.asset_maintenance) and the depreciation settings.
 */
export function AssetReports() {
  const { assets, maintenance, fetchAssets, fetchMaintenance, isLoading } = useAssetStore();
  const { settings, fetchSettings } = useAssetSettingsStore();

  useEffect(() => { fetchAssets(); fetchMaintenance(); fetchSettings(); }, [fetchAssets, fetchMaintenance, fetchSettings]);

  const currency = settings?.procurement.default_currency || 'INR';
  const money = (n: number) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n); }
    catch { return `${currency} ${Math.round(n).toLocaleString()}`; }
  };

  const active = useMemo(() => assets.filter((a) => a.status !== 'Retired' && a.status !== 'Disposed'), [assets]);

  const byGroup = (key: (a: Asset) => string) => {
    const map = new Map<string, { count: number; cost: number; book: number }>();
    for (const a of active) {
      const k = key(a);
      const cur = map.get(k) || { count: 0, cost: 0, book: 0 };
      cur.count += 1;
      cur.cost += a.purchaseCost || 0;
      if (settings) cur.book += bookValue(a, settings.depreciation) ?? 0;
      map.set(k, cur);
    }
    return [...map.entries()].sort((x, y) => y[1].cost - x[1].cost || y[1].count - x[1].count);
  };

  const byCategory = byGroup((a) => a.category || 'Uncategorised');
  const byDepartment = byGroup((a) => (a.status === 'Assigned' ? a.department || 'No department' : 'In stock / unassigned'));
  const statusCounts = assets.reduce<Record<string, number>>((m, a) => ({ ...m, [a.status]: (m[a.status] || 0) + 1 }), {});
  const totalCost = active.reduce((s, a) => s + (a.purchaseCost || 0), 0);
  const totalBook = settings ? active.reduce((s, a) => s + (bookValue(a, settings.depreciation) ?? 0), 0) : null;
  const maintenanceSpend = maintenance.filter((m) => m.status === 'completed').reduce((s, m) => s + (m.cost || 0), 0);
  const utilisation = active.length ? Math.round(((statusCounts['Assigned'] || 0) / active.length) * 100) : 0;

  const exportCsv = () => {
    const header = ['Asset code', 'Name', 'Category', 'Status', 'Assigned to', 'Department', 'Vendor', 'Purchase date', 'Purchase cost', 'Book value', 'Warranty end'];
    const rows = assets.map((a) => [
      a.asset_code, a.name, a.category, a.status, a.assignedToName, a.department, a.vendor, a.purchaseDate,
      a.purchaseCost, settings ? Math.round(bookValue(a, settings.depreciation) ?? 0) : '', a.warrantyUntil,
    ]);
    const blob = new Blob([toCsv([header, ...rows])], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const pad = (n: number) => String(n).padStart(2, '0');
    const now = new Date();
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    link.download = `asset-register_${stamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const Table = ({ title, rows }: { title: string; rows: [string, { count: number; cost: number; book: number }][] }) => (
    <Card>
      <div className="border-b border-border px-4 py-3"><h2 className="font-display text-card-title text-foreground">{title}</h2></div>
      {rows.length === 0 ? <p className="px-4 py-6 text-center text-sm text-muted-foreground">No data</p> : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
            <tr><th className="px-4 py-2 font-semibold" /><th className="px-4 py-2 text-right font-semibold">Assets</th><th className="px-4 py-2 text-right font-semibold">Cost</th><th className="px-4 py-2 text-right font-semibold">Book value</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(([name, v]) => (
              <tr key={name}>
                <td className="px-4 py-2 text-foreground">{name}</td>
                <td className="px-4 py-2 text-right tabular-nums text-foreground">{v.count}</td>
                <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{money(v.cost)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{settings ? money(v.book) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Card>
  );

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        className="mb-0"
        icon={LineChart}
        title="Asset reports"
        description={`Valuation, utilisation and spend${settings ? ' · ' + settings.depreciation.method + ' depreciation' : ''}.`}
        actions={<Button variant="secondary" onClick={exportCsv} disabled={assets.length === 0}><Download /> Export register (CSV)</Button>}
      />

      {assets.length === 0 ? (
        <EmptyState icon={<LineChart />} title={isLoading ? 'Loading…' : 'No assets registered yet'} description="Reports fill in as assets are registered." />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="p-4"><p className="text-xs text-muted-foreground">Active assets</p><p className="mt-2 font-display text-metric text-foreground">{active.length}</p><p className="mt-1 text-xs text-muted-foreground">{statusCounts['Retired'] || 0} retired</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Purchase cost</p><p className="mt-2 font-display text-metric text-foreground">{money(totalCost)}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Current book value</p><p className="mt-2 font-display text-metric text-foreground">{totalBook === null ? '—' : money(totalBook)}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Utilisation</p><p className="mt-2 font-display text-metric text-foreground">{utilisation}%</p><p className="mt-1 text-xs text-muted-foreground">Service spend {money(maintenanceSpend)}</p></Card>
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <Table title="By category" rows={byCategory} />
            <Table title="By department" rows={byDepartment} />
          </div>
          <Card className="p-4">
            <h2 className="mb-2 font-display text-card-title text-foreground">By status</h2>
            <div className="flex flex-wrap gap-4 text-sm">
              {Object.entries(statusCounts).map(([s, n]) => <span key={s} className="text-muted-foreground">{s}: <span className="font-semibold text-foreground">{n}</span></span>)}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
